<script setup lang="ts">
  import { testIds } from '@gat-ship/types'
  import {
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupTextarea,
    FieldGroup,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection
  } from '@gat-ship/vue-components'
  import { useCompanyFormModal } from '../../composables/useCompanyFormModal'

  // Single add/edit company modal. All logic — mode, form, submit dispatch, and
  // the mode-derived title/icon/description/label — lives in the composable;
  // this component only renders it. Opened by the provider via showAdd/showEdit.
  const {
    isOpen,
    form,
    title,
    icon,
    headerDescription,
    submitLabel,
    requireDirty,
    showAdd,
    showEdit
  } = useCompanyFormModal()

  defineExpose({ showAdd, showEdit })
</script>

<template>
  <Modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <component :is="icon" class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        {{ title }}
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="headerDescription">
        {{ headerDescription }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <div class="hidden" :data-testid="testIds.company.companyCode">
          {{ form.state.values.code }}
        </div>
        <FieldGroup>
          <FormField :form="form" name="name" label="Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                :data-testid="testIds.company.modal.nameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="description"
            label="Description"
            hint="Max 500 characters"
          >
            <template #default="{ field }">
              <InputGroupTextarea
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.company.modal.descriptionInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit
        :form="form"
        :label="submitLabel"
        :require-dirty="requireDirty"
      />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </Modal>
</template>
